import {
  ConfirmConsentDocumentPdfRequest,
  ConsentCategoryQuery,
  ConsentDocumentQuery,
  ConsentTemplateQuery,
  CopyConsentTemplateRequest,
  CreateConsentCategoryRequest,
  CreateConsentDocumentRequest,
  CreateConsentTemplateRequest,
  ClinicalPresentationConsentRequest,
  ClinicalPresentationDeclineRequest,
  CloseWithdrawalRequest,
  CancelConsentDocumentRequest,
  DeclineConsentRequest,
  EmployeeSignRequest,
  FamilyPresentationDeclineRequest,
  FamilySignRequest,
  ResidentConsentRequest,
  ResidentPresentationDeclineRequest,
  UpdateConsentCategoryRequest,
  UpdateConsentDocumentRequest,
  UpdateConsentTemplateRequest,
  WithdrawConsentRequest,
} from "@source/api/models/consentModels";
import {ConsentReportQuery} from "@source/api/models/consentReport";
import ConsentService, { ConsentPdfUploadBody } from "@source/api/services/consentService";

export default class ConsentController {
  static listCategories(organizationId: string, query?: ConsentCategoryQuery) {
    return ConsentService.listCategories(organizationId, query);
  }
  static getCategory(organizationId: string, categoryId: string) {
    return ConsentService.getCategory(organizationId, categoryId);
  }
  static createCategory(organizationId: string, body: CreateConsentCategoryRequest) {
    return ConsentService.createCategory(organizationId, body);
  }
  static updateCategory(organizationId: string, categoryId: string, body: UpdateConsentCategoryRequest) {
    return ConsentService.updateCategory(organizationId, categoryId, body);
  }
  static archiveCategory(organizationId: string, categoryId: string) {
    return ConsentService.archiveCategory(organizationId, categoryId);
  }
  static createSupportingDocumentUploadUrl(organizationId: string, categoryId: string) {
    return ConsentService.createSupportingDocumentUploadUrl(organizationId, categoryId);
  }
  static confirmSupportingDocument(
    organizationId: string,
    categoryId: string,
    body: ConfirmConsentDocumentPdfRequest,
  ) {
    return ConsentService.confirmSupportingDocument(organizationId, categoryId, body);
  }
  static deleteSupportingDocument(organizationId: string, categoryId: string, supportingDocumentId: string) {
    return ConsentService.deleteSupportingDocument(organizationId, categoryId, supportingDocumentId);
  }

  static listTemplates(organizationId: string, query?: ConsentTemplateQuery) {
    return ConsentService.listTemplates(organizationId, query);
  }
  static getTemplate(organizationId: string, templateId: string) {
    return ConsentService.getTemplate(organizationId, templateId);
  }
  static createTemplate(organizationId: string, body: CreateConsentTemplateRequest) {
    return ConsentService.createTemplate(organizationId, body);
  }
  static updateTemplate(organizationId: string, templateId: string, body: UpdateConsentTemplateRequest) {
    return ConsentService.updateTemplate(organizationId, templateId, body);
  }
  static copyTemplate(organizationId: string, templateId: string, body: CopyConsentTemplateRequest) {
    return ConsentService.copyTemplate(organizationId, templateId, body);
  }
  static archiveTemplate(organizationId: string, templateId: string) {
    return ConsentService.archiveTemplate(organizationId, templateId);
  }

  static listDocuments(nursingHomeId: string, query?: ConsentDocumentQuery) {
    return ConsentService.listDocuments(nursingHomeId, query);
  }
  static getDocument(nursingHomeId: string, documentId: string) {
    return ConsentService.getDocument(nursingHomeId, documentId);
  }
  static createDocument(nursingHomeId: string, body: CreateConsentDocumentRequest) {
    return ConsentService.createDocument(nursingHomeId, body);
  }
  static updateDocument(nursingHomeId: string, documentId: string, body: UpdateConsentDocumentRequest) {
    return ConsentService.updateDocument(nursingHomeId, documentId, body);
  }
  static deleteDocument(nursingHomeId: string, documentId: string) {
    return ConsentService.deleteDocument(nursingHomeId, documentId);
  }
  static cancelDocument(nursingHomeId: string, documentId: string, body: CancelConsentDocumentRequest) {
    return ConsentService.cancelDocument(nursingHomeId, documentId, body);
  }
  static sendDocument(nursingHomeId: string, documentId: string) {
    return ConsentService.sendDocument(nursingHomeId, documentId);
  }
  static remindFamilyMember(nursingHomeId: string, documentId: string, familyMemberId: string) {
    return ConsentService.remindFamilyMember(nursingHomeId, documentId, familyMemberId);
  }
  static remindEmployee(nursingHomeId: string, documentId: string, employeeId: string) {
    return ConsentService.remindEmployee(nursingHomeId, documentId, employeeId);
  }
  static remindClinicalSigner(nursingHomeId: string, documentId: string) {
    return ConsentService.remindClinicalSigner(nursingHomeId, documentId);
  }
  static createPdfUploadUrl(nursingHomeId: string, documentId: string) {
    return ConsentService.createPdfUploadUrl(nursingHomeId, documentId);
  }
  static uploadPdf(uploadUrl: string, body: ConsentPdfUploadBody) {
    return ConsentService.uploadPdf(uploadUrl, body);
  }
  static confirmPdf(nursingHomeId: string, documentId: string, body: ConfirmConsentDocumentPdfRequest) {
    return ConsentService.confirmPdf(nursingHomeId, documentId, body);
  }
  static getFinalPdf(documentId: string) {
    return ConsentService.getFinalPdf(documentId);
  }
  static getAuthorizedPreview(documentId: string) {
    return ConsentService.getAuthorizedPreview(documentId);
  }

  static listAwaitingEmployeeSignature() {
    return ConsentService.listAwaitingEmployeeSignature();
  }
  static getEmployeeDocument(documentId: string) {
    return ConsentService.getEmployeeDocument(documentId);
  }
  static signEmployeeDocument(documentId: string, body: EmployeeSignRequest) {
    return ConsentService.signEmployeeDocument(documentId, body);
  }
  static declineEmployeeDocument(documentId: string, body: DeclineConsentRequest) {
    return ConsentService.declineEmployeeDocument(documentId, body);
  }

  static getResidentPresentation(nursingHomeId: string, documentId: string) {
    return ConsentService.getResidentPresentation(nursingHomeId, documentId);
  }
  static recordResidentConsent(
    nursingHomeId: string,
    documentId: string,
    body: ResidentConsentRequest,
  ) {
    return ConsentService.recordResidentConsent(nursingHomeId, documentId, body);
  }
  static recordResidentDecline(nursingHomeId: string, documentId: string, body: ResidentPresentationDeclineRequest) {
    return ConsentService.recordResidentDecline(nursingHomeId, documentId, body);
  }

  static getClinicalPresentation(nursingHomeId: string, documentId: string) {
    return ConsentService.getClinicalPresentation(nursingHomeId, documentId);
  }
  static recordClinicalConsent(nursingHomeId: string, documentId: string, body: ClinicalPresentationConsentRequest) {
    return ConsentService.recordClinicalConsent(nursingHomeId, documentId, body);
  }
  static recordClinicalDecline(nursingHomeId: string, documentId: string, body: ClinicalPresentationDeclineRequest) {
    return ConsentService.recordClinicalDecline(nursingHomeId, documentId, body);
  }

  static getFamilyPresentation(nursingHomeId: string, documentId: string) {
    return ConsentService.getFamilyPresentation(nursingHomeId, documentId);
  }
  static recordFamilyConsent(nursingHomeId: string, documentId: string, body: FamilySignRequest) {
    return ConsentService.recordFamilyConsent(nursingHomeId, documentId, body);
  }
  static recordFamilyDecline(nursingHomeId: string, documentId: string, body: FamilyPresentationDeclineRequest) {
    return ConsentService.recordFamilyDecline(nursingHomeId, documentId, body);
  }

  static withdrawDocument(nursingHomeId: string, documentId: string, body: WithdrawConsentRequest) {
    return ConsentService.withdrawDocument(nursingHomeId, documentId, body);
  }
  static closeWithdrawalRequest(nursingHomeId: string, documentId: string, body: CloseWithdrawalRequest) {
    return ConsentService.closeWithdrawalRequest(nursingHomeId, documentId, body);
  }
  static completeDocumentReview(nursingHomeId: string, documentId: string) {
    return ConsentService.completeDocumentReview(nursingHomeId, documentId);
  }

  static downloadDocumentReport(query: ConsentReportQuery) {
    return ConsentService.downloadDocumentReport(query);
  }
  static downloadSignatureReport(query: ConsentReportQuery) {
    return ConsentService.downloadSignatureReport(query);
  }
  static downloadResidentReport(nursingHomeId: string, residentId: string, query?: ConsentReportQuery) {
    return ConsentService.downloadResidentReport(nursingHomeId, residentId, query);
  }
}
